import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Users, Music, Video, Play, Pause, UserPlus, UserCheck, MessageCircle, Pencil, Heart, Camera, Radio, Gift } from "lucide-react";
import { motion } from "framer-motion";
import { api, mediaUrl } from "@/lib/api";
import { Avatar } from "@/components/PostCard";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import { useNowPlaying } from "@/hooks/useNowPlaying";
import TipGoalBar from "@/components/TipGoalBar";
import { deadlineInfo } from "@/lib/tipgoal";
import NowPlayingBadge from "@/components/NowPlayingBadge";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import VerifiedBadge from "@/components/VerifiedBadge";

function TrackRow({ track, list, live, owner, onGoLive }) {
  const { playTrack, current, isPlaying, togglePlay } = usePlayer();
  const [liked, setLiked] = useState(track.liked);
  const [likes, setLikes] = useState(track.likes);
  const active = current?.id === track.id;
  const like = async () => {
    setLiked(!liked); setLikes(likes + (liked ? -1 : 1));
    try { const { data } = await api.post(`/like/track/${track.id}`); setLiked(data.liked); setLikes(data.likes); } catch {}
  };
  return (
    <div className="flex items-center gap-4 p-3 rounded-xl hover:bg-white/5 transition-colors group" data-testid="track-row">
      <button onClick={() => (active ? togglePlay() : playTrack(track, list))}
        className="relative w-12 h-12 rounded-lg overflow-hidden shrink-0" data-testid="track-row-play">
        {track.cover_url ? <img src={mediaUrl(track.cover_url)} alt="" className="w-full h-full object-cover" />
          : <span className="w-full h-full bg-emerald-500/15 flex items-center justify-center"><Music className="w-5 h-5 text-emerald-400" /></span>}
        <span className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          {active && isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
        </span>
      </button>
      <div className="min-w-0 flex-1">
        <p className={`font-semibold truncate ${active ? "text-emerald-400" : ""}`}>{track.title}</p>
        {live ? <NowPlayingBadge count={live} />
          : <p className="text-xs text-zinc-500 font-mono-nu uppercase tracking-wider truncate">{track.genre}{track.bpm && ` · ${track.bpm} BPM`} · {track.plays || 0} plays</p>}
      </div>
      {owner && (
        <button onClick={() => onGoLive(track)} data-testid="go-live-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/15 text-red-400 hover:bg-red-500/25 text-xs font-bold transition-colors shrink-0">
          <Radio className="w-3.5 h-3.5" /> Go Live
        </button>
      )}
      <button onClick={like} className={`flex items-center gap-1.5 text-sm ${liked ? "text-red-500" : "text-zinc-500 hover:text-red-400"} transition-colors`}>
        <Heart className={`w-4 h-4 ${liked ? "fill-red-500" : ""}`} /> <span className="font-mono-nu">{likes}</span>
      </button>
    </div>
  );
}

function EditModal({ open, onClose, artist, onSaved }) {
  const [name, setName] = useState(artist.artist_name || "");
  const [bio, setBio] = useState(artist.bio || "");
  const [genre, setGenre] = useState(artist.genre || "");
  const [busy, setBusy] = useState(false);

  const uploadImg = async (kind, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("kind", kind); fd.append("file", file);
    try { await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`${kind} updated`); onSaved(); }
    catch { toast.error("Upload failed"); }
  };

  const save = async () => {
    setBusy(true);
    try { await api.put("/profile", { artist_name: name, bio, genre }); toast.success("Profile updated"); onSaved(); onClose(); }
    catch { toast.error("Could not save"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#181824] border-white/10 text-white" data-testid="edit-profile-modal">
        <DialogHeader><DialogTitle className="font-display">Edit Profile</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-3">
            <label className="flex-1 flex items-center gap-2 justify-center py-2 rounded-lg border border-white/10 hover:border-emerald-500/50 cursor-pointer text-sm" data-testid="edit-avatar-upload">
              <Camera className="w-4 h-4" /> Avatar
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg("avatar", e.target.files[0])} />
            </label>
            <label className="flex-1 flex items-center gap-2 justify-center py-2 rounded-lg border border-white/10 hover:border-emerald-500/50 cursor-pointer text-sm" data-testid="edit-cover-upload">
              <Camera className="w-4 h-4" /> Cover
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg("cover", e.target.files[0])} />
            </label>
          </div>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Artist name" data-testid="edit-name"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-emerald-500/50" />
          <input value={genre} onChange={(e) => setGenre(e.target.value)} placeholder="Genre" data-testid="edit-genre"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-emerald-500/50" />
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Bio" rows={3} data-testid="edit-bio"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-emerald-500/50 resize-none" />
          <button onClick={save} disabled={busy} data-testid="edit-save"
            className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-black font-bold transition-colors disabled:opacity-50">Save Changes</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TipModal({ open, onClose, artist }) {
  const [amount, setAmount] = useState(5);
  const [busy, setBusy] = useState(false);
  const presets = [3, 5, 10, 25];
  const tip = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tips/checkout", { artist_id: artist.id, amount: Number(amount), origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start tip"); setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#181824] border-white/10 text-white" data-testid="tip-modal">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Gift className="w-5 h-5 text-emerald-400" /> Tip {artist.artist_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400">Support this artist directly. They keep 90%.</p>
        <div className="flex gap-2 mt-2">
          {presets.map((p) => (
            <button key={p} onClick={() => setAmount(p)} data-testid={`tip-preset-${p}`}
              className={`flex-1 py-2 rounded-lg font-bold text-sm transition-colors ${Number(amount) === p ? "bg-emerald-500 text-black" : "bg-white/5 hover:bg-white/10"}`}>${p}</button>
          ))}
        </div>
        <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="tip-amount"
          className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-emerald-500/50 mt-2" />
        <button onClick={tip} disabled={busy} data-testid="tip-submit"
          className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-black font-bold transition-colors disabled:opacity-50 mt-2">
          {busy ? "Redirecting…" : `Send $${Number(amount) || 0} tip`}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function GoLiveModal({ track, onClose, onStart }) {
  const [price, setPrice] = useState(0);
  const [busy, setBusy] = useState(false);
  if (!track) return null;
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post("/parties", { track_id: track.id, ticket_price: Number(price) || 0 }); onStart(data.id); }
    catch { toast.error("Could not start the party"); setBusy(false); }
  };
  return (
    <Dialog open={!!track} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#181824] border-white/10 text-white" data-testid="golive-modal">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Radio className="w-5 h-5 text-red-400" /> Go Live · {track.title}</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400">Set a ticket price for fans to join, or leave 0 for a free party. You keep 90% of ticket sales.</p>
        <label className="text-xs text-zinc-400 mt-2">Ticket price (USD)</label>
        <input type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="golive-price"
          className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-emerald-500/50" placeholder="0 = free" />
        <button onClick={go} disabled={busy} data-testid="golive-start"
          className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-black font-bold transition-colors disabled:opacity-50 mt-2">
          {busy ? "Starting…" : Number(price) > 0 ? `Go Live · $${Number(price)} tickets` : "Go Live (free)"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

export default function Profile() {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const nowPlaying = useNowPlaying();
  const [artist, setArtist] = useState(null);
  const [following, setFollowing] = useState(false);
  const [followers, setFollowers] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [goLiveTrack, setGoLiveTrack] = useState(null);

  const load = useCallback(() => {
    api.get(`/artists/${artistId}`).then(({ data }) => {
      setArtist(data); setFollowing(data.is_following); setFollowers(data.followers);
    }).catch(() => {});
  }, [artistId]);
  useEffect(() => { load(); }, [load]);

  if (!artist) return <p className="text-zinc-500 text-center py-16 font-mono-nu">loading profile…</p>;
  const isMe = artist.id === user.id;

  const toggleFollow = async () => {
    try { const { data } = await api.post(`/artists/${artist.id}/follow`); setFollowing(data.is_following); setFollowers(data.followers); } catch {}
  };

  const startParty = (track) => setGoLiveTrack(track);

  return (
    <div>
      {/* Cover */}
      <div className="h-48 sm:h-64 relative overflow-hidden -mt-4">
        {artist.cover_url ? <img src={mediaUrl(artist.cover_url)} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full bg-gradient-to-br from-violet-600/40 via-[#12121A] to-emerald-600/20" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0E] via-transparent to-transparent" />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-16 relative">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="rounded-full border-4 border-[#0A0A0E]">
            <div className="rounded-full ring-2 ring-emerald-500/80 glow-emerald">
              <Avatar artist={artist} size={120} />
            </div>
          </div>
          <div className="flex-1 pb-2">
            <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-2">{artist.artist_name}<VerifiedBadge show={artist.is_subscribed} size={24} /></h1>
            <p className="text-zinc-500 font-mono-nu">@{artist.handle}{artist.genre && ` · ${artist.genre}`}</p>
          </div>
          <div className="flex items-center gap-3 pb-2">
            {isMe ? (
              <button onClick={() => setEditOpen(true)} data-testid="edit-profile-btn"
                className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 hover:border-emerald-500/60 font-semibold text-sm transition-colors">
                <Pencil className="w-4 h-4" /> Edit Profile
              </button>
            ) : (
              <>
                <button onClick={toggleFollow} data-testid="profile-follow-btn"
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-colors ${following ? "bg-white/10 hover:bg-white/20" : "bg-emerald-500 text-black hover:bg-emerald-600"}`}>
                  {following ? <><UserCheck className="w-4 h-4" /> Following</> : <><UserPlus className="w-4 h-4" /> Follow</>}
                </button>
                <button onClick={() => navigate(`/messages/${artist.id}`)} data-testid="profile-message-btn"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 hover:border-emerald-500/60 font-semibold text-sm transition-colors">
                  <MessageCircle className="w-4 h-4" /> Message
                </button>
                <button onClick={() => setTipOpen(true)} data-testid="profile-tip-btn"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-sm transition-colors">
                  <Gift className="w-4 h-4" /> Tip
                </button>
              </>
            )}
          </div>
        </div>

        {artist.bio && <p className="mt-4 text-zinc-300 max-w-2xl">{artist.bio}</p>}

        <div className="mt-4 flex items-center gap-6 text-sm">
          <span className="flex items-center gap-2"><b className="font-mono-nu">{followers}</b> <span className="text-zinc-500">Followers</span></span>
          <span className="flex items-center gap-2"><b className="font-mono-nu">{artist.following}</b> <span className="text-zinc-500">Following</span></span>
          <span className="flex items-center gap-2"><b className="font-mono-nu">{artist.tracks?.length || 0}</b> <span className="text-zinc-500">Tracks</span></span>
        </div>

        {artist.tip_goal_cents > 0 && (
          <div className="mt-5 max-w-2xl">
            <TipGoalBar goalCents={artist.tip_goal_cents} progressCents={artist.tip_progress_cents} label={artist.tip_goal_label} deadline={artist.tip_goal_deadline} compact />
            {!isMe && !deadlineInfo(artist.tip_goal_deadline)?.ended && (
              <button onClick={() => setTipOpen(true)} data-testid="tip-goal-cta"
                className="mt-3 flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-sm transition-colors">
                <Gift className="w-4 h-4" /> Chip in a tip
              </button>
            )}
          </div>
        )}

        <Tabs defaultValue="tracks" className="mt-8">
          <TabsList className="bg-black/40 border border-white/10">
            <TabsTrigger value="tracks" data-testid="profile-tab-tracks" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-black"><Music className="w-4 h-4 mr-1" /> Tracks</TabsTrigger>
            <TabsTrigger value="videos" data-testid="profile-tab-videos" className="data-[state=active]:bg-violet-500 data-[state=active]:text-white"><Video className="w-4 h-4 mr-1" /> Videos</TabsTrigger>
          </TabsList>
          <TabsContent value="tracks" className="mt-4">
            {(artist.tracks || []).length === 0 ? <p className="text-zinc-500 py-8 text-center">No tracks yet.</p>
              : <div className="nu-card p-2">{artist.tracks.map((t) => <TrackRow key={t.id} track={t} list={artist.tracks} live={nowPlaying[t.id]} owner={isMe} onGoLive={startParty} />)}</div>}
          </TabsContent>
          <TabsContent value="videos" className="mt-4">
            {(artist.videos || []).length === 0 ? <p className="text-zinc-500 py-8 text-center">No videos yet.</p>
              : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {artist.videos.map((v) => (
                    <div key={v.id} className="nu-card overflow-hidden">
                      <video src={mediaUrl(v.video_url)} poster={mediaUrl(v.thumb_url)} controls className="w-full aspect-video bg-black" />
                      <p className="p-3 font-semibold truncate">{v.title}</p>
                    </div>
                  ))}
                </div>
              )}
          </TabsContent>
        </Tabs>
      </div>

      {isMe && <EditModal open={editOpen} onClose={() => setEditOpen(false)} artist={artist} onSaved={load} />}
      {!isMe && <TipModal open={tipOpen} onClose={() => setTipOpen(false)} artist={artist} />}
      <GoLiveModal track={goLiveTrack} onClose={() => setGoLiveTrack(null)} onStart={(pid) => navigate(`/party/${pid}`)} />
    </div>
  );
}
